/**
 * Screen-local API module for the Paid Social stats report (task 035-10).
 *
 * Codes against task 035-09's pinned contract
 * (`tasks/035_stats_screen/task_09_paid_social_query_sonnet.md` §Contract,
 * lines 34-65). Task 09 need not have landed — this module and its test mock
 * the response shape; the DTOs below are the api-local (A3) types copied
 * verbatim from the pinned contract.
 *
 * Deliberately outside `lib/api.ts` (ADR-010 gap #2 -> A2 (b)): same base-URL
 * expression and `{ data }` envelope handling as `reports-api.ts` /
 * `cto-stats-api.ts` / `custom-optin-stats-api.ts`.
 */

import type { CampaignStatus } from '@leads-platform/shared';
import { apiFetch } from '../../lib/api';

/**
 * One traffic-source row of `GET /admin/reports/paid-social-stats` (pinned
 * contract). `utmSource` carries the RAW source — `''` for the no-source
 * grain; `(no utm_source)` is THIS screen's label and is never the DTO value,
 * because the same field is the export scope this screen passes to task 07's
 * endpoint (`utmSource=` selects the no-source rows).
 */
export interface PaidSocialSourceRow {
  utmSource: string;
  leads: number;
  uniqueLeads: number;
  success: number;
  duplicate: number;
  unchecked: number;
  rejected: number;
  newToFilePct: number | null;
  cost: number | null; // Σ campaign cost under the source; null when every campaign price is null
}

/** One (utm_source, campaign) row of `GET /admin/reports/paid-social-stats` (pinned contract). */
export interface PaidSocialCampaignRow {
  utmSource: string;
  campaignId: string;
  campaignName: string;
  pageTitle: string | null;
  pageSlug: string | null;
  startDate: string | null;
  endDate: string | null;
  status: CampaignStatus;
  pricePerLead: number | null;
  leads: number;
  uniqueLeads: number;
  success: number;
  duplicate: number;
  unchecked: number;
  rejected: number;
  newToFilePct: number | null;
  cost: number | null; // pricePerLead × uniqueLeads
}

/** Grand totals over the whole selection (pinned contract): distinct counts recomputed, sums summed. */
export interface PaidSocialTotals {
  leads: number;
  uniqueLeads: number;
  success: number;
  duplicate: number;
  unchecked: number;
  rejected: number;
  newToFilePct: number | null;
  cost: number | null; // Σ of the SOURCE rows' cost — never price × selection unique (CtoTotals.costOfUnique rule)
}

export interface PaidSocialStatsResponse {
  from: string;
  to: string;
  timezone: string;
  includeTest: boolean;
  sources: PaidSocialSourceRow[]; // ORDER BY utmSource
  campaigns: PaidSocialCampaignRow[]; // ORDER BY utmSource, campaignName, campaignId
  totals: PaidSocialTotals;
}

/**
 * Screen filter state. `campaignId === ''` means the filter is off and is
 * omitted from the query string; `from`/`to`/`includeTest` are always sent
 * (co-op's `CoopCountsFilters` convention, `reports-api.ts`).
 */
export interface PaidSocialStatsFilters {
  from: string;
  to: string;
  includeTest: boolean;
  campaignId: string;
}

/**
 * Task 07's endpoint (`campaign-leads.csv`) is live (the tail's last task):
 * every export button on this screen renders.
 * `paid-social-stats.export-urls.test.tsx` pins the state explicitly through
 * its hoisted `vi.mock`.
 */
export const CAMPAIGN_LEADS_EXPORT_AVAILABLE = true;

export type PaidSocialLeadsVariant = 'all' | 'unique' | 'rejected';

/**
 * `campaign-leads.csv` export scope for one level-2 row: a (utmSource,
 * campaign) row is that source's slice of the campaign, never the whole
 * campaign — `utmSource` is always sent, `''` for the no-source row (README
 * decision 9, r4 F-2).
 */
export interface PaidSocialLeadsExportParams {
  campaignId: string;
  utmSource: string;
  from: string;
  to: string;
  includeTest: boolean;
  variant: PaidSocialLeadsVariant;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/** `GET /admin/reports/paid-social-stats` query -- `coopReportQuerySchema.pick({ from, to, includeTest, campaignId })`. */
function buildPaidSocialStatsQuery(filters: PaidSocialStatsFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.set('from', filters.from);
  params.set('to', filters.to);
  params.set('includeTest', filters.includeTest ? 'true' : 'false');
  if (filters.campaignId) params.set('campaignId', filters.campaignId);
  return params;
}

/** Absolute Paid Social stats URL, ready for `fetch`. */
export function paidSocialStatsUrl(filters: PaidSocialStatsFilters): string {
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/paid-social-stats?${buildPaidSocialStatsQuery(filters).toString()}`;
}

/**
 * Absolute `campaign-leads.csv` export URL for `window.open` -- param order
 * matches task 10 §Scope 1's spec verbatim:
 * `campaignId&utmSource&from&to&includeTest&variant`. `utmSource` is always
 * set, empty for the no-source row, so the file is that row's slice of the
 * campaign — never the whole campaign.
 */
export function paidSocialLeadsExportUrl(params: PaidSocialLeadsExportParams): string {
  const query = new URLSearchParams();
  query.set('campaignId', params.campaignId);
  query.set('utmSource', params.utmSource);
  query.set('from', params.from);
  query.set('to', params.to);
  query.set('includeTest', params.includeTest ? 'true' : 'false');
  query.set('variant', params.variant);
  return `${API_BASE_URL}${ADMIN_PREFIX}/reports/campaign-leads.csv?${query.toString()}`;
}

/**
 * `message` of a Nest error body without a cast (`reports-api.ts`'s idiom):
 * narrow the unknown JSON with the `in` operator, then the string check.
 */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null && 'message' in body) {
    const message = body.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/** Success envelope -- matches the api's `ApiResponse<T>` (conventions §DTO). */
interface PaidSocialStatsEnvelope {
  data: PaidSocialStatsResponse;
}

/** Fetch the Paid Social stats for one filter set. Non-2xx throws with the body's message (or the status text). */
export async function fetchPaidSocialStats(
  filters: PaidSocialStatsFilters
): Promise<PaidSocialStatsResponse> {
  const response = await apiFetch(paidSocialStatsUrl(filters));
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    throw new Error(messageFromBody(body, response.statusText));
  }
  const envelope: PaidSocialStatsEnvelope = await response.json();
  return envelope.data;
}
